import { useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, CornerLeftUp, GripVertical, LayoutGrid, List, Plus } from 'lucide-react';
import { createBaseplanCode, getCodeBoard, toCodeWrite, updateBaseplanCode } from '@/api/baseplanCodes';
import { ApiError } from '@/api/client';
import { AlertDialog } from '@/components/ui/AlertDialog';
import type { BaseplanCode, CodeBoard as CodeBoardData } from '@/types/oscar';
import { Badge, ReportingBranchList } from './ReportingBranchList';

const BOARD_KEY = ['locations', 'codes'] as const;
const inputCls = 'rounded border border-border bg-white px-2 py-1.5 text-sm';

type PendingMove = { code: BaseplanCode; toAreaId: number | null };

/**
 * Locations › Regions & areas › Codes — the reporting hierarchy (oscar.BaseplanCode;
 * Data-Model §4.5.2). Every Baseplan code (branch, minor code, debtors division -
 * one code list) sits in ONE area, which gives its region, so reports get one
 * path per code (ref.vw_Branch_v2). Drag a code onto an area to move it; codes
 * with no area wait in the Unallocated tray so nothing drops out of a report
 * silently. Click a code to edit its name, parent (portables -> main branch),
 * branch flag, sort and active state.
 *
 * Physical places are separate: which codes a location holds is on the location.
 *
 * Shown to users as "reporting branches" (Anthony, 25/10/2026: "that's the parameter
 * it drives"); the code stays oscar.BaseplanCode. A List view shows the same rows as
 * a table (ReportingBranchList.tsx); moving between areas is the board's job.
 */
export function CodeBoard({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: BOARD_KEY, queryFn: ({ signal }) => getCodeBoard(signal) });
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [pending, setPending] = useState<PendingMove | null>(null);
  const [editing, setEditing] = useState<BaseplanCode | 'new' | null>(null);
  const [view, setView] = useState<'board' | 'list'>('board');

  const move = useMutation({
    mutationFn: ({ code, toAreaId }: PendingMove) => updateBaseplanCode(code.Code, { ...toCodeWrite(code), AreaId: toAreaId }),
    onSuccess: () => { setPending(null); void qc.invalidateQueries({ queryKey: BOARD_KEY }); },
  });

  const data = query.data;
  const term = search.trim().toLowerCase();
  const visible = (c: BaseplanCode) =>
    (showInactive || c.IsActive) && (!term || `${c.Code} ${c.Description}`.toLowerCase().includes(term));
  const byArea = useMemo(() => {
    const map = new Map<number | null, BaseplanCode[]>();
    for (const c of data?.Codes ?? []) map.set(c.AreaId, [...(map.get(c.AreaId) ?? []), c]);
    return map;
  }, [data]);
  const areaName = (id: number | null) =>
    id === null ? 'Unallocated' : data?.Areas.find((a) => a.AreaId === id)?.AreaDesc ?? `Area ${id}`;
  const regionOfArea = (id: number | null) => {
    const regionId = data?.Areas.find((a) => a.AreaId === id)?.RegionId;
    return data?.Regions.find((r) => r.RegionId === regionId)?.RegionDesc ?? 'no region';
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor));
  const onDragEnd = (e: DragEndEvent) => {
    const over = e.over?.data.current as { areaId: number | null } | undefined;
    const code = e.active.data.current?.code as BaseplanCode | undefined;
    if (!over || !code || over.areaId === code.AreaId) return;
    move.reset();
    setPending({ code, toAreaId: over.areaId });
  };

  if (query.isLoading) return <p className="text-sm text-darkGrey/60">Loading…</p>;
  if (query.isError || !data) return <p className="text-sm text-red">Could not load the reporting branches.</p>;

  const regions = [...data.Regions, ...(data.Areas.some((a) => a.RegionId === null) ? [{ RegionId: -1, RegionDesc: 'No region', RegionSort: null }] : [])];
  const unallocated = (byArea.get(null) ?? []).filter(visible);

  return (
    <div>
      <p className="mb-3 text-sm text-darkGrey/80">
        Each reporting branch — a Baseplan branch, minor code or debtors division — sits in one area, and the area gives its region.
        Reports use this path (<span className="font-mono text-xs">ref.vw_Branch_v2</span>).
        {isAdmin ? ' On the board, drag a code onto an area to move it; click a code to edit it.' : ''}
      </p>
      {!data.BaseplanFlagsAvailable && (
        <p className="mb-3 text-xs text-darkGrey/60">Baseplan&apos;s branch / minor / division lists could not be read, so those badges are hidden.</p>
      )}
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search reporting branches
          <input aria-label="Search reporting branches" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Code or name" className={`${inputCls} mt-1 font-normal`} />
        </label>
        <label className="inline-flex items-center gap-1.5 pb-2 text-xs text-darkGrey">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive
        </label>
        <div className="inline-flex rounded border border-border bg-white p-0.5 text-sm" role="group" aria-label="View">
          {([['board', 'Board', LayoutGrid], ['list', 'List', List]] as const).map(([id, label, Icon]) => (
            <button
              key={id}
              type="button"
              aria-pressed={view === id}
              onClick={() => setView(id)}
              className={`inline-flex items-center gap-1 rounded px-2.5 py-1 font-medium ${view === id ? 'bg-navy text-white' : 'text-darkGrey/70 hover:text-navy'}`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden /> {label}
            </button>
          ))}
        </div>
        {isAdmin && (
          <button type="button" onClick={() => setEditing('new')} className="inline-flex items-center gap-1 rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90">
            <Plus className="h-4 w-4" aria-hidden /> Add reporting branch
          </button>
        )}
      </div>

      {view === 'list' && (
        <ReportingBranchList board={data} codes={data.Codes.filter(visible)} flags={data.BaseplanFlagsAvailable} onOpen={setEditing} />
      )}

      {view === 'board' && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
            <div className="flex-1 space-y-4">
              {regions.map((r) => {
                const areas = data.Areas.filter((a) => (r.RegionId === -1 ? a.RegionId === null : a.RegionId === r.RegionId));
                if (areas.length === 0) return null;
                return (
                  <section key={r.RegionId} aria-label={`Region ${r.RegionDesc}`}>
                    <h3 className="mb-1.5 font-heading text-sm font-bold uppercase tracking-wide text-navy">{r.RegionDesc}</h3>
                    <div className="space-y-1.5">
                      {areas.map((a) => (
                        <AreaRow
                          key={a.AreaId}
                          areaId={a.AreaId}
                          name={a.AreaDesc}
                          codes={(byArea.get(a.AreaId) ?? []).filter(visible)}
                          total={(byArea.get(a.AreaId) ?? []).filter((c) => c.IsActive).length}
                          expanded={!collapsed.has(a.AreaId) || term !== ''}
                          onToggle={() => setCollapsed((prev) => { const n = new Set(prev); if (n.has(a.AreaId)) n.delete(a.AreaId); else n.add(a.AreaId); return n; })}
                          isAdmin={isAdmin}
                          flags={data.BaseplanFlagsAvailable}
                          onOpen={setEditing}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
            <aside className="w-full shrink-0 lg:sticky lg:top-4 lg:w-80">
              <Tray codes={unallocated} isAdmin={isAdmin} flags={data.BaseplanFlagsAvailable} onOpen={setEditing} />
            </aside>
          </div>
        </DndContext>
      )}

      {pending && (
        <AlertDialog
          open
          onOpenChange={(o) => { if (!o) setPending(null); }}
          title={`Move ${pending.code.Code} to ${areaName(pending.toAreaId)}?`}
          description={
            pending.toAreaId === null
              ? `${pending.code.Code} ${pending.code.Description} leaves ${areaName(pending.code.AreaId)}. Reports show it with no area or region until it is placed again.`
              : `${pending.code.Code} ${pending.code.Description} moves from ${areaName(pending.code.AreaId)} to ${areaName(pending.toAreaId)} (${regionOfArea(pending.toAreaId)}). Reports group it there from now on.`
          }
          confirmLabel="Move"
          closeOnConfirm={false}
          onConfirm={() => move.mutate(pending)}
        >
          {move.isError && <p role="alert" className="text-sm text-red">{move.error instanceof ApiError ? move.error.message : 'Could not move that code.'}</p>}
        </AlertDialog>
      )}

      {editing && (
        <CodeEditor
          code={editing === 'new' ? null : editing}
          board={data}
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void qc.invalidateQueries({ queryKey: BOARD_KEY }); }}
        />
      )}
    </div>
  );
}

function AreaRow({
  areaId, name, codes, total, expanded, onToggle, isAdmin, flags, onOpen,
}: {
  areaId: number;
  name: string;
  codes: BaseplanCode[];
  total: number;
  expanded: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  flags: boolean;
  onOpen: (c: BaseplanCode) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `area-${areaId}`, data: { areaId } });
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <div ref={setNodeRef} className={`rounded border bg-white shadow-sm transition-colors ${isOver ? 'border-orange ring-2 ring-orange/40' : 'border-border'}`}>
      <button type="button" onClick={onToggle} aria-expanded={expanded} className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-[#F4F6F9]">
        <Chevron className="h-4 w-4 shrink-0 text-darkGrey/50" aria-hidden />
        <span className="text-sm font-semibold text-navy">{name}</span>
        <span className="font-mono text-[10px] text-darkGrey/50">area {areaId}</span>
        <span className="ml-auto shrink-0 rounded-full bg-[#E6ECF2] px-2 py-0.5 text-xs font-semibold text-navy" title="Active codes">{total}</span>
      </button>
      {expanded && (
        <ul className="flex flex-wrap gap-1.5 border-t border-border px-3 py-2">
          {codes.map((c) => <CodeChip key={c.Code} code={c} isAdmin={isAdmin} flags={flags} onOpen={onOpen} />)}
          {codes.length === 0 && <li className="text-xs text-darkGrey/50">No codes{total > 0 ? ' match' : ''}.</li>}
        </ul>
      )}
    </div>
  );
}

function Tray({ codes, isAdmin, flags, onOpen }: { codes: BaseplanCode[]; isAdmin: boolean; flags: boolean; onOpen: (c: BaseplanCode) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'area-none', data: { areaId: null } });
  return (
    <div ref={setNodeRef} className={`rounded border border-dashed p-3 transition-colors ${isOver ? 'border-orange bg-orange/5 ring-2 ring-orange/40' : 'border-border bg-[#F4F6F9]'}`}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
        Unallocated <span className="text-darkGrey/40">({codes.length})</span>
      </h3>
      <p className="mt-0.5 text-xs text-darkGrey/60">No area yet, so reports show no area or region for these. Drag each onto its area.</p>
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {codes.map((c) => <CodeChip key={c.Code} code={c} isAdmin={isAdmin} flags={flags} onOpen={onOpen} />)}
        {codes.length === 0 && <li className="text-sm text-darkGrey/60">None — every code has an area.</li>}
      </ul>
    </div>
  );
}

function CodeChip({ code, isAdmin, flags, onOpen }: { code: BaseplanCode; isAdmin: boolean; flags: boolean; onOpen: (c: BaseplanCode) => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `code-${code.Code}`, data: { code }, disabled: !isAdmin });
  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined;
  const title = `${code.Code} ${code.Description} — ${code.Locations} location(s), ${code.HomeBranchPeople} people with it as primary branch`;
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1 rounded border px-1.5 py-1 text-xs ${code.IsActive ? 'border-border bg-white' : 'border-dashed border-border bg-[#F4F6F9] text-darkGrey/50'} ${isDragging ? 'opacity-50' : ''}`}
      title={title}
    >
      {isAdmin && (
        <button type="button" {...attributes} {...listeners} aria-label={`Drag ${code.Code}`} className="cursor-grab touch-none text-darkGrey/40 hover:text-darkGrey focus:outline-none focus-visible:ring-2 focus-visible:ring-navy">
          <GripVertical className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      <button type="button" onClick={() => onOpen(code)} className="flex items-center gap-1 text-left hover:underline" aria-label={`Open ${code.Code} ${code.Description}`}>
        <span className="font-mono font-semibold text-navy">{code.Code}</span>
        <span className="max-w-[12rem] truncate">{code.Description}</span>
        {code.ParentCode && <span className="inline-flex items-center text-darkGrey/50" title={`Rolls up to ${code.ParentCode}`}><CornerLeftUp className="h-3 w-3" aria-hidden />{code.ParentCode}</span>}
        {code.IsBranch && <Badge text="Branch" />}
        {flags && code.InGLMinor && <Badge text="M" title="Baseplan minor code" />}
        {flags && code.InDebtorsDivision && <Badge text="D" title="Debtors division" />}
      </button>
    </li>
  );
}


// ── Edit / add one code ─────────────────────────────────────────────────────

const codeSchema = z.object({
  Code: z.string().trim().min(1, 'Code is required').max(20, 'At most 20').regex(/^[A-Za-z0-9]+$/, 'Letters and digits only'),
  Description: z.string().trim().min(1, 'Name is required').max(100, 'At most 100 characters'),
  AreaId: z.string(),
  ParentCode: z.string(),
  IsBranch: z.boolean(),
  SortOrder: z.string().trim().max(10, 'At most 10 characters'),
  ExcludeFromDailySales: z.boolean(),
  IsActive: z.boolean(),
  Notes: z.string().trim().max(400, 'At most 400 characters'),
});
type CodeForm = z.infer<typeof codeSchema>;

function CodeEditor({
  code, board, isAdmin, onClose, onSaved,
}: {
  code: BaseplanCode | null;
  board: CodeBoardData;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<CodeForm>({
    resolver: zodResolver(codeSchema),
    defaultValues: {
      Code: code?.Code ?? '',
      Description: code?.Description ?? '',
      AreaId: code?.AreaId == null ? '' : String(code.AreaId),
      ParentCode: code?.ParentCode ?? '',
      IsBranch: code?.IsBranch ?? false,
      SortOrder: code?.SortOrder ?? '',
      ExcludeFromDailySales: code?.ExcludeFromDailySales ?? false,
      IsActive: code?.IsActive ?? true,
      Notes: code?.Notes ?? '',
    },
  });
  const save = useMutation({
    mutationFn: (d: CodeForm) => {
      const body = {
        Description: d.Description.trim(),
        AreaId: d.AreaId === '' ? null : Number(d.AreaId),
        ParentCode: d.ParentCode === '' ? null : d.ParentCode,
        IsBranch: d.IsBranch,
        SortOrder: d.SortOrder.trim() || null,
        ExcludeFromDailySales: d.ExcludeFromDailySales,
        IsActive: d.IsActive,
        Notes: d.Notes.trim() || null,
      };
      return code ? updateBaseplanCode(code.Code, body) : createBaseplanCode({ ...body, Code: d.Code.trim() });
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save the code.'),
  });
  const err = formState.errors;
  const parents = board.Codes.filter((c) => c.Code !== code?.Code && c.IsActive).sort((a, b) => a.Code.localeCompare(b.Code));

  return (
    <AlertDialog
      open
      wide
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={code ? `${code.Code} ${code.Description}` : 'Add a reporting branch'}
      description={code
        ? `${code.Locations} location(s) hold it; ${code.HomeBranchPeople} active people have it as their primary branch.`
        : 'A Baseplan code (branch, minor code or debtors division) Baseplan has added. Put it in its area so reports pick it up.'}
      confirmLabel={isAdmin ? 'Save' : 'Close'}
      hideCancel={!isAdmin}
      closeOnConfirm={false}
      onConfirm={() => (isAdmin ? void handleSubmit((d) => { setError(null); save.mutate(d); })() : onClose())}
    >
      <form aria-label="Code" onSubmit={(e) => e.preventDefault()}>
        <fieldset disabled={!isAdmin || save.isPending} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {!code && (
            <label className="flex flex-col text-xs font-semibold text-darkGrey">
              Code
              <input {...register('Code')} className={`${inputCls} mt-1 font-mono font-normal`} />
              {err.Code && <span className="mt-1 font-normal text-red">{err.Code.message}</span>}
            </label>
          )}
          <label className="flex flex-col text-xs font-semibold text-darkGrey">
            Name in reports
            <input {...register('Description')} className={`${inputCls} mt-1 font-normal`} />
            {err.Description && <span className="mt-1 font-normal text-red">{err.Description.message}</span>}
          </label>
          <label className="flex flex-col text-xs font-semibold text-darkGrey">
            Area
            <select {...register('AreaId')} className={`${inputCls} mt-1 font-normal`}>
              <option value="">— Unallocated —</option>
              {board.Areas.map((a) => (
                <option key={a.AreaId} value={String(a.AreaId)}>
                  {a.AreaDesc} ({board.Regions.find((r) => r.RegionId === a.RegionId)?.RegionDesc ?? 'no region'})
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-xs font-semibold text-darkGrey">
            Rolls up to
            <select {...register('ParentCode')} className={`${inputCls} mt-1 font-normal`}>
              <option value="">— nothing (a main code) —</option>
              {parents.map((p) => <option key={p.Code} value={p.Code}>{p.Code} {p.Description}</option>)}
            </select>
          </label>
          <label className="flex flex-col text-xs font-semibold text-darkGrey">
            Sort
            <input {...register('SortOrder')} className={`${inputCls} mt-1 font-normal`} />
            {err.SortOrder && <span className="mt-1 font-normal text-red">{err.SortOrder.message}</span>}
          </label>
          <label className="flex flex-col text-xs font-semibold text-darkGrey sm:col-span-2">
            Notes
            <input {...register('Notes')} className={`${inputCls} mt-1 font-normal`} />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-darkGrey"><input type="checkbox" {...register('IsBranch')} /> An operating hire branch</label>
          <label className="inline-flex items-center gap-2 text-sm text-darkGrey"><input type="checkbox" {...register('ExcludeFromDailySales')} /> Exclude from daily sales</label>
          <label className="inline-flex items-center gap-2 text-sm text-darkGrey"><input type="checkbox" {...register('IsActive')} /> Active</label>
        </fieldset>
      </form>
      {error && <p role="alert" className="mt-2 text-sm text-red">{error}</p>}
    </AlertDialog>
  );
}
